import { Box } from "@mui/material";
import { useCallback, useMemo } from "react";

import { losAcquisitionsApi } from "../../api/losAcquisitionsApi";
import { losPipelineApi } from "../../api/losPipelineApi";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { PIPELINE_KEYS } from "../../pipelines/pipelineConfig";
import { status as statusColors } from "../../theme/colors";
import { buildAcquisitionsColumnDefs } from "../AcquisitionsPipelineScreen/acquisitionsColumnDefs";
import PipelineTableScreen from "./PipelineTableScreen";

// Which pipeline a row belongs to: every underwriting deal (including ones at
// Clear To Close or Closed) is "Pre-Close", every acquisitions-pipeline deal is
// "Acquisitions".
const STAGE = {
  PRE_CLOSE: "Pre-Close",
  ACQUISITIONS: "Acquisitions",
};
const STAGE_OPTIONS = Object.values(STAGE);
const STAGE_TONE = {
  [STAGE.PRE_CLOSE]: statusColors.neutral,
  [STAGE.ACQUISITIONS]: statusColors.info,
};

// `_pipelineKey` lets the shared table open each row in its own drilldown
// (/loan vs /acquisition) and save inline edits through the right router.
const tagRows = (deals, pipelineKey, stageOf) =>
  (Array.isArray(deals) ? deals : []).filter(Boolean).map((d) => ({
    ...d,
    _pipelineKey: pipelineKey,
    pipelineStage: stageOf(d),
  }));

const CellRendererPipelineStage = (p) => {
  if (p?.node?.group || !p?.value) return null;
  const tone = STAGE_TONE[p.value] || statusColors.neutral;
  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      <Box
        component="span"
        sx={{
          px: 0.875,
          py: "1px",
          borderRadius: "999px",
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.6,
          whiteSpace: "nowrap",
          bgcolor: tone.bg,
          color: tone.fg,
        }}
      >
        {p.value}
      </Box>
    </Box>
  );
};

// Acquisition-only fields worth having on the combined board. Available from
// the column menu; hidden by default so the preclose layout stays familiar.
const ACQUISITION_EXTRA_FIELDS = [
  "sellerCorrespondentName",
  "currentUPB",
  "noteRate",
  "purchasePriceAmount",
  "tradeId",
  "tradeStatus",
  "settlementDate",
];

/**
 * Full Pipeline — every underwriting deal (the whole Pre-Close board) plus
 * every acquisition, on one board. Both pipelines are fetched in parallel; if
 * one fails the other still loads. The status strip slices by pipeline stage.
 */
const FullPipelineScreen = () => {
  useDocumentTitle("Full Pipeline");

  const extraColumnDefs = useMemo(() => {
    const acquisitionCols = buildAcquisitionsColumnDefs().filter((c) =>
      ACQUISITION_EXTRA_FIELDS.includes(c?.field),
    );
    return [
      {
        headerName: "Pipeline",
        field: "pipelineStage",
        width: 130,
        minWidth: 110,
        flex: 0,
        filter: "agSetColumnFilter",
        cellRenderer: CellRendererPipelineStage,
      },
      ...acquisitionCols.map((c) => ({ ...c, hide: true })),
    ];
  }, []);

  const fetchData = useCallback(({ onSuccessFn, onFailFn, onErrorFn }) => {
    const onError = onFailFn || onErrorFn || (() => {});
    Promise.allSettled([
      losPipelineApi.getUnderwritingPipeline(),
      losAcquisitionsApi.getAcquisitionsPipeline(),
    ]).then(([uw, acq]) => {
      if (uw.status === "rejected" && acq.status === "rejected") {
        onError(uw.reason);
        return;
      }
      if (uw.status === "rejected")
        console.error("Full Pipeline: underwriting fetch failed", uw.reason);
      if (acq.status === "rejected")
        console.error("Full Pipeline: acquisitions fetch failed", acq.reason);
      onSuccessFn?.([
        ...tagRows(uw.value, PIPELINE_KEYS.UNDERWRITING, () => STAGE.PRE_CLOSE),
        ...tagRows(
          acq.value,
          PIPELINE_KEYS.ACQUISITIONS,
          () => STAGE.ACQUISITIONS,
        ),
      ]);
    });
  }, []);

  return (
    <PipelineTableScreen
      title="Full Pipeline"
      storageKeyPrefix="full-pipeline"
      // Saved views are stored on the user profile so they survive a cleared
      // browser / another machine. Board-local list (no layoutScope) — see the
      // full-pipeline-* entries in useUserPreferences' SYNCED_KEYS.
      syncLayoutsToProfile
      fetchData={fetchData}
      statusField="pipelineStage"
      statusOptions={STAGE_OPTIONS}
      extraColumnDefs={extraColumnDefs}
    />
  );
};

export default FullPipelineScreen;
